import { useEffect, useState, useRef, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import { Building2, Users, Calendar, MapPin } from 'lucide-react'
import { getStats } from '@/data/locations'

interface CounterData {
  end: number
  suffix: string
  labelKey: string
  sublabelKey: string
  icon: typeof Building2
  color: string
}

// Function to get dynamic counters config
const getCountersConfig = (): CounterData[] => {
  const locationStats = getStats()

  return [
    {
      end: locationStats.total,
      suffix: '',
      labelKey: 'counters.managements.label',
      sublabelKey: 'counters.managements.sublabel',
      icon: Building2,
      color: 'from-amber-500 to-orange-600',
    },
    {
      end: 110,
      suffix: '+',
      labelKey: 'counters.staff.label',
      sublabelKey: 'counters.staff.sublabel',
      icon: Users,
      color: 'from-blue-500 to-indigo-600',
    },
    {
      end: 15,
      suffix: '',
      labelKey: 'counters.experience.label',
      sublabelKey: 'counters.experience.sublabel',
      icon: Calendar,
      color: 'from-emerald-500 to-teal-600',
    },
    {
      end: 2,
      suffix: '',
      labelKey: 'counters.locations.label',
      sublabelKey: 'counters.locations.sublabel',
      icon: MapPin,
      color: 'from-purple-500 to-violet-600',
    },
  ]
}

const CounterCard = memo(function CounterCard({
  data,
  index,
  t,
}: {
  data: CounterData
  index: number
  t: (key: string) => string
}) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-50px' })
  const Icon = data.icon

  useEffect(() => {
    if (!isInView) return

    const duration = 1500
    const steps = 40
    const increment = data.end / steps
    let current = 0

    const timer = setInterval(() => {
      current += increment
      if (current >= data.end) {
        setCount(data.end)
        clearInterval(timer)
      } else {
        setCount(Math.floor(current))
      }
    }, duration / steps)

    return () => clearInterval(timer)
  }, [data.end, isInView])

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="relative group h-full"
    >
      <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white border border-gray-100 p-4 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 group-hover:-translate-y-1 h-full flex flex-col">
        {/* Background gradient orb */}
        <div
          className={`absolute -top-12 -right-12 w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br ${data.color} opacity-10 blur-2xl group-hover:opacity-20 transition-opacity`}
        />

        {/* Icon */}
        <div
          className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${data.color} flex items-center justify-center mb-3 sm:mb-4 shadow-md`}
        >
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>

        {/* Counter */}
        <div className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-1 sm:mb-2">
          {count}
          <span className={`bg-gradient-to-r ${data.color} bg-clip-text text-transparent`}>
            {data.suffix}
          </span>
        </div>

        {/* Labels */}
        <div className="mt-auto">
          <div className="text-sm sm:text-base font-semibold text-gray-900 mb-0.5 sm:mb-1">{t(data.labelKey)}</div>
          <div className="text-xs sm:text-sm text-gray-500 min-h-[2rem] sm:min-h-[2.5rem]">{t(data.sublabelKey)}</div>
        </div>
      </div>
    </motion.div>
  )
})

export function CountersSection() {
  const { t } = useTranslation('home')

  // Get dynamic counters config
  const countersConfig = useMemo(() => getCountersConfig(), [])

  return (
    <section className="section-padding bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 opacity-40 blur-3xl -translate-y-1/2" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 opacity-40 blur-3xl translate-y-1/2" />

      <div className="container-custom relative">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {countersConfig.map((counter, index) => (
            <CounterCard key={index} data={counter} index={index} t={t} />
          ))}
        </div>
      </div>
    </section>
  )
}
